import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import {
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getInsightsVisitAvgByWeek } from "../../actions/acc_actions";
import Loading from "../../utils/Loading";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
);

const ActiveUserChart = ({ dateTo, dateFrom, selectedSchema }) => {
  const { t } = useTranslation();
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const { isLoading, data: insightsData } = useQuery({
    queryKey: [
      "insightsVisitAvgByWeek",
      businessSlug,
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getInsightsVisitAvgByWeek({
        to: dateTo,
        from: dateFrom,
        refresh: false,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: Boolean(dateTo && dateFrom && businessSlug && selectedSchema),
    keepPreviousData: true,
  });

  if (isLoading) return <Loading />;

  const chartData = insightsData?.body?.data ?? [];

  if (!chartData.length) {
    return <div>{t("no_data_available")}</div>;
  }

  // CSV Download Function
  const handleDownloadCSV = () => {
    const headers = [
      "Week",
      "Start Of Week",
      "Active Customers",
      "Actual Visits",
      "Avg Visits Per Active Customer",
    ];

    const rows = chartData.map((item) => [
      item.week,
      item.start_of_week_iso,
      item.active_customers,
      item.actual_visits,
      item.avg_visits_per_active_customer,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `active-user-data-${dateFrom}-to-${dateTo}.csv`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const datasets = [
    {
      label: t("active_customers"),
      data: chartData.map((item) => ({
        x: item.start_of_week_iso?.value,
        y: item.active_customers,
      })),
      borderColor: "#1890ff",
      backgroundColor: "rgba(24,144,255,.2)",
      tension: 0.4,
    },
    {
      label: t("actual_visits"),
      data: chartData.map((item) => ({
        x: item.start_of_week_iso?.value,
        y: item.actual_visits,
      })),
      borderColor: "#52c41a",
      backgroundColor: "rgba(82,196,26,.2)",
      tension: 0.4,
    },
    {
      label: t("avg_visits_per_customer"),
      data: chartData.map((item) => ({
        x: item.start_of_week_iso?.value,
        y: item.avg_visits_per_active_customer,
      })),
      borderColor: "#fa8c16",
      backgroundColor: "rgba(250,140,22,.2)",
      tension: 0.4,
      yAxisID: "y2",
    },
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "week",
          tooltipFormat: "MMM dd, yyyy",
          displayFormats: { week: "MMM dd" },
        },
        title: {
          display: true,
          text: t("week_starting"),
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("customers_visits"),
        },
      },
      y2: {
        beginAtZero: true,
        position: "right",
        grid: {
          drawOnChartArea: false,
        },
        title: {
          display: true,
          text: t("avg_visits_per_customer"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
  };

  return (
    <div className="active-user-chart-container chart">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h3 style={{ margin: 0 }}>{t("active_user_data")}</h3>

        <Button icon={<DownloadOutlined />} onClick={handleDownloadCSV}>
          {t("download_data")}
        </Button>
      </div>

      <div className="chart-container" style={{ height: "400px" }}>
        <Line data={{ datasets }} options={chartOptions} />
      </div>
    </div>
  );
};

export default ActiveUserChart;
